import * as THREE from 'three';
import { InputManager } from '../input/InputManager';
import { EventEmitter } from '../core/EventEmitter';

enum TutorialPhase {
  FIND_KEY,
  OPEN_DOOR,
  PICK_UP_GUN,
  PICK_UP_ARMOR,
  SHOOT_TARGETS,
  EXIT_DOOR,
  COMPLETE,
}

export class Tutorial {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private input: InputManager;
  private events: EventEmitter;
  private phase: TutorialPhase = TutorialPhase.FIND_KEY;

  private room: THREE.Object3D[] = [];
  private colliders: THREE.Box3[] = [];
  private key: THREE.Group | null = null;
  private door: THREE.Mesh | null = null;
  private doorColliderIndex = -1;
  private gun: THREE.Group | null = null;
  private armorPickup: THREE.Group | null = null;
  private targets: THREE.Mesh[] = [];
  private targetsHit = 0;
  private hasKey = false;
  private hasGun = false;
  private hasArmor = false;
  private raycaster = new THREE.Raycaster();
  private resolve: (() => void) | null = null;
  private msgElement: HTMLElement | null = null;
  private hintElement: HTMLElement | null = null;
  private doorAnimProgress = -1;
  private exitDoor: THREE.Mesh | null = null;
  private exitDoorColliderIndex = -1;
  private exitDoorAnimProgress = -1;

  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera, input: InputManager, events: EventEmitter) {
    this.scene = scene;
    this.camera = camera;
    this.input = input;
    this.events = events;
  }

  start(): Promise<void> {
    return new Promise((resolve) => {
      this.resolve = resolve;
      this.buildTutorialRoom();
      this.phase = TutorialPhase.FIND_KEY;
      this.camera.position.set(0, 1.7, 0);
      this.camera.rotation.set(0, 0, 0);
      this.showMessage('鍵を探して部屋から出ろ');
      this.showHint('WASD: 移動 / マウス: 視点 / E: インタラクト');
    });
  }

  private buildTutorialRoom(): void {
    const M = (color: number, rough = 0.8, metal = 0) =>
      new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: metal });

    // ===== LOCKED ROOM (8x8) =====
    // Floor
    this.addBox(M(0x2d2d2d, 0.9), [0, -0.05, 0], [8, 0.1, 8], false);
    // Ceiling
    this.addBox(M(0x222222, 0.95), [0, 3.5, 0], [8, 0.1, 8], false);
    // Walls
    this.addBox(M(0x3a3a3a), [0, 1.75, -4], [8, 3.5, 0.3], true);
    this.addBox(M(0x3a3a3a), [0, 1.75, 4], [8, 3.5, 0.3], true);
    this.addBox(M(0x3a3a3a), [-4, 1.75, 0], [0.3, 3.5, 8], true);
    // Right wall with hole for door
    this.addBox(M(0x3a3a3a), [4, 1.75, -2.5], [0.3, 3.5, 3], true);
    this.addBox(M(0x3a3a3a), [4, 1.75, 2.5], [0.3, 3.5, 3], true);
    this.addBox(M(0x3a3a3a), [4, 2.85, 0], [0.3, 1.3, 2], true); // above door

    // Door
    const doorMat = M(0x5a4a3a, 0.7, 0.1);
    this.door = new THREE.Mesh(new THREE.BoxGeometry(0.12, 2.2, 1.8), doorMat);
    this.door.position.set(3.9, 1.1, 0);
    this.door.userData.isTutorial = true;
    this.scene.add(this.door);
    this.room.push(this.door);
    const doorBox = new THREE.Box3().setFromObject(this.door);
    this.doorColliderIndex = this.colliders.length;
    this.colliders.push(doorBox);

    // Door handle
    const handle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.015, 0.015, 0.08, 6),
      M(0x888888, 0.3, 0.8)
    );
    handle.rotation.z = Math.PI / 2;
    handle.position.set(3.82, 1.0, 0.5);
    handle.userData.isTutorial = true;
    this.scene.add(handle);
    this.room.push(handle);

    // Door frame
    const frameMat = M(0x555555, 0.5, 0.4);
    this.addBox(frameMat, [3.95, 0, 0.95], [0.2, 2.2, 0.06], false);
    this.addBox(frameMat, [3.95, 0, -0.95], [0.2, 2.2, 0.06], false);
    this.addBox(frameMat, [3.95, 2.2, 0], [0.2, 0.06, 2], false);

    // "LOCKED" sign on door
    const signMat = new THREE.MeshStandardMaterial({
      color: 0xff0000, emissive: 0xff0000, emissiveIntensity: 0.5,
    });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.12, 0.01), signMat);
    sign.position.set(3.8, 1.6, 0);
    sign.userData.isTutorial = true;
    sign.userData.isLockSign = true;
    this.scene.add(sign);
    this.room.push(sign);

    // Room light (bright enough to see)
    this.addLight(0xffaa66, 3, 10, [0, 3, 0]);
    this.addLight(0xffffff, 0.5, 6, [0, 2.5, 0]); // fill

    // Desk
    this.addBox(M(0x445566, 0.6), [-2, 0.45, -2.5], [1.5, 0.9, 0.7], true);
    // Chair
    this.addBox(M(0x333333), [1.5, 0.25, 1.5], [0.5, 0.5, 0.5], true);

    // ===== SHELF (key is on this shelf) =====
    const shelfWoodMat = M(0x5a3a1a, 0.75, 0.05);
    // Shelf frame (tall back panel)
    this.addBox(shelfWoodMat, [-3, 1.0, 2.65], [0.8, 2.0, 0.06], true);
    // Shelf sides
    this.addBox(shelfWoodMat, [-3.38, 1.0, 2.4], [0.06, 2.0, 0.55], false);
    this.addBox(shelfWoodMat, [-2.62, 1.0, 2.4], [0.06, 2.0, 0.55], false);
    // Shelf boards (3 levels)
    this.addBox(shelfWoodMat, [-3, 0.05, 2.4], [0.82, 0.04, 0.55], false); // bottom
    this.addBox(shelfWoodMat, [-3, 0.7, 2.4], [0.82, 0.04, 0.55], false);  // middle
    this.addBox(shelfWoodMat, [-3, 1.35, 2.4], [0.82, 0.04, 0.55], false); // key level
    this.addBox(shelfWoodMat, [-3, 2.0, 2.4], [0.82, 0.04, 0.55], false);  // top

    // Items on shelf (decoration)
    // Binder on bottom shelf
    this.addBox(M(0x2244aa, 0.7), [-3.1, 0.22, 2.4], [0.25, 0.3, 0.2], false);
    this.addBox(M(0x22aa44, 0.7), [-2.85, 0.22, 2.4], [0.2, 0.3, 0.2], false);
    // Box on middle shelf
    this.addBox(M(0x887766, 0.8), [-3.1, 0.85, 2.35], [0.3, 0.25, 0.3], false);
    // Bottle on middle shelf
    const bottle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.035, 0.15, 8),
      new THREE.MeshStandardMaterial({ color: 0x335533, transparent: true, opacity: 0.5, roughness: 0.1 })
    );
    bottle.position.set(-2.75, 0.8, 2.35);
    bottle.userData.isTutorial = true;
    this.scene.add(bottle);
    this.room.push(bottle);

    // Paper on floor
    for (let i = 0; i < 3; i++) {
      const paper = new THREE.Mesh(
        new THREE.PlaneGeometry(0.15, 0.2),
        M(0xddddcc, 0.9)
      );
      paper.rotation.x = -Math.PI / 2;
      paper.rotation.z = Math.random() * Math.PI;
      paper.position.set(-1 + Math.random() * 2, 0.005, -1 + Math.random() * 2);
      paper.userData.isTutorial = true;
      this.scene.add(paper);
      this.room.push(paper);
    }

    // ===== KEY on shelf (golden, glowing) =====
    this.key = new THREE.Group();
    const keyMat = new THREE.MeshStandardMaterial({
      color: 0xffd700, roughness: 0.3, metalness: 0.8,
      emissive: 0xffd700, emissiveIntensity: 0.8,
    });
    const keyBody = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.08, 0.15), keyMat);
    this.key.add(keyBody);
    const keyRing = new THREE.Mesh(new THREE.TorusGeometry(0.03, 0.006, 6, 12), keyMat);
    keyRing.position.set(0, 0, 0.09);
    keyRing.rotation.x = Math.PI / 2;
    this.key.add(keyRing);
    for (let i = 0; i < 3; i++) {
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.015, 0.025, 0.01), keyMat);
      tooth.position.set(0.012, 0, -0.05 + i * 0.03);
      this.key.add(tooth);
    }
    // Key on the upper shelf board (y=1.35 + key half height)
    this.key.position.set(-3, 1.45, 2.35);
    this.key.userData.isTutorial = true;
    this.scene.add(this.key);
    this.room.push(this.key);
    // Key glow light
    this.addLight(0xffd700, 1.5, 4, [-3, 1.6, 2.35]);

    // ===== AREA BEYOND DOOR =====
    // Floor
    this.addBox(M(0x2d2d2d, 0.9), [10, -0.05, 0], [14, 0.1, 8], false);
    this.addBox(M(0x222222, 0.95), [10, 3.5, 0], [14, 0.1, 8], false);
    // Walls
    this.addBox(M(0x3a3a3a), [10, 1.75, -4], [14, 3.5, 0.3], true);
    this.addBox(M(0x3a3a3a), [10, 1.75, 4], [14, 3.5, 0.3], true);
    // Back wall with exit door hole
    this.addBox(M(0x3a3a3a), [17, 1.75, -2.5], [0.3, 3.5, 3], true);
    this.addBox(M(0x3a3a3a), [17, 1.75, 2.5], [0.3, 3.5, 3], true);
    this.addBox(M(0x3a3a3a), [17, 2.85, 0], [0.3, 1.3, 2], true);

    // Exit door (locked until targets destroyed)
    const exitDoorMat = M(0x555555, 0.5, 0.4);
    this.exitDoor = new THREE.Mesh(new THREE.BoxGeometry(0.12, 2.2, 1.8), exitDoorMat);
    this.exitDoor.position.set(16.9, 1.1, 0);
    this.exitDoor.userData.isTutorial = true;
    this.scene.add(this.exitDoor);
    this.room.push(this.exitDoor);
    this.exitDoorColliderIndex = this.colliders.length;
    this.colliders.push(new THREE.Box3().setFromObject(this.exitDoor));

    // EXIT sign (red, locked)
    const exitSign = new THREE.Mesh(
      new THREE.BoxGeometry(0.5, 0.15, 0.01),
      new THREE.MeshStandardMaterial({ color: 0xff0000, emissive: 0xff0000, emissiveIntensity: 0.8 })
    );
    exitSign.position.set(16.8, 2.3, 0);
    exitSign.rotation.y = Math.PI / 2;
    exitSign.userData.isTutorial = true;
    exitSign.userData.isExitSign = true;
    this.scene.add(exitSign);
    this.room.push(exitSign);

    // Door frame
    this.addBox(M(0x666666, 0.4, 0.5), [16.95, 0, 0.95], [0.2, 2.2, 0.06], false);
    this.addBox(M(0x666666, 0.4, 0.5), [16.95, 0, -0.95], [0.2, 2.2, 0.06], false);
    this.addBox(M(0x666666, 0.4, 0.5), [16.95, 2.2, 0], [0.2, 0.06, 2], false);

    // Light beyond exit door (green = go)
    this.addLight(0x44ff44, 2, 6, [18, 2, 0]);
    // Lights
    this.addLight(0xaaccff, 3, 12, [7, 3, 0]);
    this.addLight(0xaaccff, 2, 10, [13, 3, 0]);

    // ===== GUN ON TABLE =====
    this.addBox(M(0x445566, 0.6), [6, 0.45, 0], [1, 0.9, 0.6], true);
    this.gun = new THREE.Group();
    // Slide (dark steel)
    const slideMat = M(0x2a2a2a, 0.35, 0.8);
    const slide = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.08, 0.22), slideMat);
    slide.position.y = 0.01;
    this.gun.add(slide);
    // Frame (slightly lighter)
    const frameMat2 = M(0x333333, 0.4, 0.7);
    const frame = new THREE.Mesh(new THREE.BoxGeometry(0.048, 0.06, 0.18), frameMat2);
    frame.position.set(0, -0.04, 0.01);
    this.gun.add(frame);
    // Grip (textured dark)
    const gripMat = M(0x1a1a1a, 0.75, 0.1);
    const grip = new THREE.Mesh(new THREE.BoxGeometry(0.042, 0.1, 0.05), gripMat);
    grip.position.set(0, -0.09, 0.06);
    grip.rotation.x = -0.2;
    this.gun.add(grip);
    // Barrel
    const barrelMat = M(0x2a2a2a, 0.25, 0.85);
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.12, 8), barrelMat);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0.02, -0.16);
    this.gun.add(barrel);
    // Trigger guard
    const guardMat = M(0x2a2a2a, 0.4, 0.7);
    const guard = new THREE.Mesh(new THREE.TorusGeometry(0.02, 0.004, 4, 8, Math.PI), guardMat);
    guard.position.set(0, -0.06, 0.02);
    guard.rotation.y = Math.PI / 2;
    this.gun.add(guard);
    // Trigger
    const trigger = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.025, 0.005), M(0x222222, 0.5, 0.6));
    trigger.position.set(0, -0.06, 0.02);
    this.gun.add(trigger);
    // Sight (front)
    const sight = new THREE.Mesh(new THREE.BoxGeometry(0.01, 0.015, 0.01), slideMat);
    sight.position.set(0, 0.055, -0.09);
    this.gun.add(sight);

    this.gun.position.set(6, 1.0, 0);
    this.gun.userData.isTutorial = true;
    this.scene.add(this.gun);
    this.room.push(this.gun);
    // White/warm light to highlight gun (no blue)
    this.addLight(0xffeedd, 1.5, 4, [6, 1.3, 0]);

    // ===== ARMOR PICKUP (防弾チョッキ / Bulletproof Vest) =====
    this.armorPickup = new THREE.Group();
    const vestMainMat = new THREE.MeshStandardMaterial({ color: 0x2a3a2a, roughness: 0.85, metalness: 0.05 });
    const vestPadMat = new THREE.MeshStandardMaterial({ color: 0x1a2a1a, roughness: 0.9 });
    const vestStrapMat = new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.7 });
    const vestPlateMat = new THREE.MeshStandardMaterial({ color: 0x555555, roughness: 0.4, metalness: 0.6 });
    // Main vest body (front + back panels)
    const vestFront = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.38, 0.06), vestMainMat);
    vestFront.position.z = 0.06;
    this.armorPickup.add(vestFront);
    const vestBack = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.38, 0.06), vestMainMat);
    vestBack.position.z = -0.06;
    this.armorPickup.add(vestBack);
    // Side connections
    for (const side of [-1, 1]) {
      const sidePanel = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.32, 0.14), vestMainMat);
      sidePanel.position.set(side * 0.16, -0.02, 0);
      this.armorPickup.add(sidePanel);
    }
    // Shoulder straps
    for (const side of [-1, 1]) {
      const strap = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.04, 0.22), vestStrapMat);
      strap.position.set(side * 0.1, 0.2, 0);
      this.armorPickup.add(strap);
    }
    // Front armor plate (ceramic plate insert)
    const plate = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.28, 0.02), vestPlateMat);
    plate.position.set(0, 0, 0.1);
    this.armorPickup.add(plate);
    // Front padding bumps
    for (let py = -1; py <= 1; py++) {
      const pad = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.08, 0.02), vestPadMat);
      pad.position.set(0, py * 0.1, 0.12);
      this.armorPickup.add(pad);
    }
    // Collar (neck guard)
    const collar = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.06, 0.16), vestMainMat);
    collar.position.set(0, 0.22, 0);
    this.armorPickup.add(collar);
    // Velcro patch area (front)
    const velcro = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.05, 0.005), new THREE.MeshStandardMaterial({ color: 0x444433, roughness: 1.0 }));
    velcro.position.set(0, 0.1, 0.13);
    this.armorPickup.add(velcro);

    this.armorPickup.position.set(9, 1.0, -2);
    this.armorPickup.userData.isTutorial = true;
    this.scene.add(this.armorPickup);
    this.room.push(this.armorPickup);
    // Pedestal for armor
    this.addBox(M(0x445566, 0.6), [9, 0.45, -2], [0.8, 0.9, 0.6], true);
    this.addLight(0x44ddff, 2, 5, [9, 1.5, -2]);

    // ===== TARGET PANELS (zombie silhouettes) =====
    // Targets face the player (player approaches from -X, so targets face -X)
    const targetPositions = [[12, 1.2, -2], [14, 1.2, 0], [12, 1.2, 2]];
    for (const pos of targetPositions) {
      // Wooden board - thin on X axis so it faces the player
      const target = new THREE.Mesh(
        new THREE.BoxGeometry(0.08, 1.8, 0.8),
        new THREE.MeshStandardMaterial({ color: 0x885533, roughness: 0.8 })
      );
      target.position.set(pos[0], pos[1], pos[2]);
      target.userData.isTutorial = true;
      target.userData.isTarget = true;
      this.scene.add(target);
      this.room.push(target);
      this.targets.push(target);

      // Zombie silhouette on front face
      const silhouette = new THREE.Mesh(
        new THREE.BoxGeometry(0.01, 1.5, 0.5),
        new THREE.MeshStandardMaterial({ color: 0x222222 })
      );
      silhouette.position.set(pos[0] - 0.05, pos[1], pos[2]);
      silhouette.userData.isTutorial = true;
      this.scene.add(silhouette);
      this.room.push(silhouette);

      // Head circle (red bullseye) facing player
      const bullseye = new THREE.Mesh(
        new THREE.CircleGeometry(0.15, 12),
        new THREE.MeshStandardMaterial({ color: 0xff2222, emissive: 0xff0000, emissiveIntensity: 0.8 })
      );
      bullseye.position.set(pos[0] - 0.055, pos[1] + 0.55, pos[2]);
      bullseye.rotation.y = Math.PI / 2; // face -X
      bullseye.userData.isTutorial = true;
      this.scene.add(bullseye);
      this.room.push(bullseye);

      // Stand
      const stand = new THREE.Mesh(
        new THREE.BoxGeometry(0.08, 1.2, 0.08),
        M(0x555555, 0.5, 0.5)
      );
      stand.position.set(pos[0] + 0.1, 0.3, pos[2]);
      stand.userData.isTutorial = true;
      this.scene.add(stand);
      this.room.push(stand);
    }
  }

  private addBox(mat: THREE.Material, pos: number[], size: number[], isCollider: boolean): THREE.Mesh {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(size[0], size[1], size[2]), mat);
    mesh.position.set(pos[0], pos[1], pos[2]);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.isTutorial = true;
    this.scene.add(mesh);
    this.room.push(mesh);
    if (isCollider) this.colliders.push(new THREE.Box3().setFromObject(mesh));
    return mesh;
  }

  private addLight(color: number, intensity: number, dist: number, pos: number[]): THREE.PointLight {
    const light = new THREE.PointLight(color, intensity, dist);
    light.position.set(pos[0], pos[1], pos[2]);
    light.userData.isTutorial = true;
    this.scene.add(light);
    this.room.push(light);
    return light;
  }

  private xzDist(a: THREE.Vector3, b: THREE.Vector3): number {
    const dx = a.x - b.x;
    const dz = a.z - b.z;
    return Math.sqrt(dx * dx + dz * dz);
  }

  update(dt: number): boolean {
    const interact = this.input.isKeyJustPressed('KeyE');

    // Door open animation
    if (this.doorAnimProgress >= 0 && this.door) {
      this.doorAnimProgress += dt * 1.5;
      if (this.doorAnimProgress >= 1) {
        this.scene.remove(this.door);
        this.door = null;
        this.doorAnimProgress = -1;
      } else {
        // Swing open (rotate around hinge)
        this.door.rotation.y = this.doorAnimProgress * Math.PI * 0.45;
        this.door.position.x = 3.9 + Math.sin(this.doorAnimProgress * Math.PI * 0.45) * 0.5;
        this.door.position.z = -Math.sin(this.doorAnimProgress * Math.PI * 0.45) * 0.8;
      }
    }

    switch (this.phase) {
      case TutorialPhase.FIND_KEY: {
        // Rotate key
        if (this.key) {
          this.key.rotation.y += dt * 2;
          this.key.position.y = 1.45 + Math.sin(performance.now() * 0.003) * 0.03;
        }
        // Near key? Show prompt
        if (this.key) {
          const dist = this.xzDist(this.camera.position, this.key.position);
          if (dist < 2.0) {
            this.showHint('[E] 鍵を拾う');
            if (interact) {
              this.hasKey = true;
              this.scene.remove(this.key);
              this.key = null;
              this.showMessage('鍵を手に入れた！ドアに向かえ');
              this.showHint('[E] ドアの前で使う');
              this.phase = TutorialPhase.OPEN_DOOR;
              // Remove lock sign
              this.room.forEach(obj => {
                if (obj.userData.isLockSign) {
                  (obj as THREE.Mesh).material = new THREE.MeshStandardMaterial({
                    color: 0x00ff00, emissive: 0x00ff00, emissiveIntensity: 0.5,
                  });
                }
              });
            }
          } else {
            this.showHint('机の上の鍵を探せ');
          }
        }
        break;
      }

      case TutorialPhase.OPEN_DOOR: {
        if (this.door && this.hasKey) {
          const dist = this.xzDist(this.camera.position, this.door.position);
          if (dist < 2.5) {
            this.showHint('[E] ドアを開ける');
            if (interact) {
              // Remove door collider
              if (this.doorColliderIndex >= 0) {
                this.colliders.splice(this.doorColliderIndex, 1);
                this.doorColliderIndex = -1;
              }
              // Start door open animation
              this.doorAnimProgress = 0;
              this.showMessage('銃を取れ');
              this.showHint('奥のテーブルの銃を拾え');
              this.phase = TutorialPhase.PICK_UP_GUN;
            }
          } else {
            this.showHint('ドアに近づけ');
          }
        }
        break;
      }

      case TutorialPhase.PICK_UP_GUN: {
        if (this.gun) {
          this.gun.rotation.y += dt * 2;
          this.gun.position.y = 1.0 + Math.sin(performance.now() * 0.003) * 0.05;
        }
        if (this.gun) {
          const dist = this.xzDist(this.camera.position, this.gun.position);
          if (dist < 2.0) {
            this.showHint('[E] 銃を拾う');
            if (interact) {
              this.hasGun = true;
              this.scene.remove(this.gun);
              this.gun = null;
              this.showMessage('アーマーを装備しろ！');
              this.showHint('光っているアーマーに近づいて [E]');
              this.phase = TutorialPhase.PICK_UP_ARMOR;
              this.events.emit('tutorial:gunPickedUp');
            }
          } else {
            this.showHint('銃に近づけ');
          }
        }
        break;
      }

      case TutorialPhase.PICK_UP_ARMOR: {
        if (this.armorPickup) {
          this.armorPickup.rotation.y += dt * 2;
          this.armorPickup.position.y = 1.0 + Math.sin(performance.now() * 0.003) * 0.05;
          const dist = this.xzDist(this.camera.position, this.armorPickup.position);
          if (dist < 2.0) {
            this.showHint('[E] アーマーを装備');
            if (interact) {
              this.hasArmor = true;
              this.scene.remove(this.armorPickup);
              this.armorPickup = null;
              this.showMessage('アーマー装備完了！\n的を撃て！ 3体を破壊しろ');
              this.showHint('左クリック: 射撃 / R: リロード');
              this.phase = TutorialPhase.SHOOT_TARGETS;
              this.events.emit('tutorial:armorPickedUp');
            }
          } else {
            this.showHint('アーマーに近づけ');
          }
        }
        break;
      }

      case TutorialPhase.SHOOT_TARGETS: {
        if (this.input.isMouseJustPressed(0) && this.hasGun) {
          const dir = new THREE.Vector3();
          this.camera.getWorldDirection(dir);
          this.raycaster.set(this.camera.position, dir);
          this.raycaster.far = 30;

          const hits = this.raycaster.intersectObjects(this.targets, false);
          if (hits.length > 0) {
            const hitTarget = hits[0].object as THREE.Mesh;
            // Destroy target
            const mat = hitTarget.material as THREE.MeshStandardMaterial;
            mat.color.setHex(0x111111);
            mat.emissive.setHex(0x441100);
            mat.emissiveIntensity = 0.3;
            hitTarget.scale.set(0.8, 0.3, 0.8);
            hitTarget.position.y = 0.2;
            hitTarget.rotation.x = Math.PI / 2 - 0.3;

            this.targetsHit++;
            this.targets = this.targets.filter(t => t !== hitTarget);

            if (this.targetsHit >= 3) {
              this.showMessage('よくやった！ 出口のドアへ向かえ');
              this.showHint('出口ドアに近づいて [E]');
              this.phase = TutorialPhase.EXIT_DOOR;
              // Unlock exit door - change sign to green
              this.room.forEach(obj => {
                if (obj.userData.isExitSign) {
                  const m = (obj as THREE.Mesh).material as THREE.MeshStandardMaterial;
                  m.color.setHex(0x00ff00);
                  m.emissive.setHex(0x00ff00);
                }
              });
            } else {
              this.showMessage(`的を撃て！ 残り ${3 - this.targetsHit}`);
            }
          }
        }
        break;
      }

      case TutorialPhase.EXIT_DOOR: {
        // Animate exit door opening
        if (this.exitDoorAnimProgress >= 0 && this.exitDoor) {
          this.exitDoorAnimProgress += dt * 1.5;
          if (this.exitDoorAnimProgress >= 1) {
            this.scene.remove(this.exitDoor);
            this.exitDoor = null;
            this.exitDoorAnimProgress = -1;
            // Complete!
            this.showMessage('準備完了！ ミッション開始...');
            this.showHint('');
            this.phase = TutorialPhase.COMPLETE;
            setTimeout(() => {
              this.cleanup();
              if (this.resolve) this.resolve();
            }, 2000);
          } else {
            this.exitDoor.rotation.y = this.exitDoorAnimProgress * Math.PI * 0.45;
            this.exitDoor.position.x = 16.9 + Math.sin(this.exitDoorAnimProgress * Math.PI * 0.45) * 0.5;
            this.exitDoor.position.z = Math.sin(this.exitDoorAnimProgress * Math.PI * 0.45) * 0.8;
          }
        } else if (this.exitDoor) {
          const dist = this.xzDist(this.camera.position, this.exitDoor.position);
          if (dist < 3.0) {
            this.showHint('[E] ドアを開けて出発');
            if (interact) {
              // Remove collider
              if (this.exitDoorColliderIndex >= 0) {
                this.colliders.splice(this.exitDoorColliderIndex, 1);
                this.exitDoorColliderIndex = -1;
              }
              this.exitDoorAnimProgress = 0;
            }
          } else {
            this.showHint('出口ドアに向かえ →');
          }
        }
        break;
      }
    }

    return this.phase === TutorialPhase.COMPLETE;
  }

  getColliders(): THREE.Box3[] {
    return this.colliders;
  }

  private showMessage(text: string): void {
    if (this.msgElement) this.msgElement.remove();
    this.msgElement = document.createElement('div');
    this.msgElement.style.cssText = `
      position:fixed;top:25%;left:50%;transform:translateX(-50%);
      color:#fff;font-size:1.5rem;font-weight:bold;text-align:center;
      white-space:pre-line;text-shadow:0 0 20px rgba(0,0,0,1),0 2px 8px rgba(0,0,0,0.9);
      z-index:30;pointer-events:none;
      background:rgba(0,0,0,0.5);padding:15px 30px;border-radius:4px;
      border:1px solid rgba(255,255,255,0.15);
    `;
    this.msgElement.textContent = text;
    document.body.appendChild(this.msgElement);

    // Auto fade after 4s
    setTimeout(() => {
      if (this.msgElement) {
        this.msgElement.style.transition = 'opacity 1s';
        this.msgElement.style.opacity = '0.3';
      }
    }, 4000);
  }

  private showHint(text: string): void {
    if (!this.hintElement) {
      this.hintElement = document.createElement('div');
      this.hintElement.style.cssText = `
        position:fixed;bottom:20%;left:50%;transform:translateX(-50%);
        color:#ffcc00;font-size:1rem;text-align:center;
        text-shadow:0 0 10px rgba(0,0,0,1);
        z-index:30;pointer-events:none;
      `;
      document.body.appendChild(this.hintElement);
    }
    this.hintElement.textContent = text;
  }

  cleanup(): void {
    for (const obj of this.room) {
      this.scene.remove(obj);
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        if (obj.material instanceof THREE.Material) obj.material.dispose();
      }
      if (obj instanceof THREE.Light) obj.dispose();
    }
    this.room = [];
    this.colliders = [];
    this.targets = [];
    if (this.msgElement) { this.msgElement.remove(); this.msgElement = null; }
    if (this.hintElement) { this.hintElement.remove(); this.hintElement = null; }
  }
}
